import { z } from 'zod';

/**
 * Options for parsing variant attachments
 */
export type ParseVariantAttachmentOptions<T = Record<string, unknown>> = {
  /** Zod schema to validate the parsed data */
  schema?: z.ZodType<T>;
  /** Whether to throw on parse errors (default: false, returns null) */
  throwOnError?: boolean;
  /** Custom error handler */
  onError?: (error: Error) => void;
};

/**
 * Default schema for variant attachments when no schema is provided
 * Accepts any valid JSON object
 */
export const defaultVariantAttachmentSchema = z.record(z.unknown());

/**
 * Parse and validate a variant attachment JSON string
 *
 * @param attachment The JSON string from variantAttachment
 * @param options Parsing and validation options
 * @returns Parsed and validated data, or null if parsing/validation fails
 *
 * @example
 * ```typescript
 * // With default schema (Record<string, unknown>)
 * const data = parseVariantAttachment(attachment);
 *
 * // With custom Zod schema
 * const ThemeSchema = z.object({
 *   primaryColor: z.string(),
 *   fontSize: z.number(),
 * });
 * const theme = parseVariantAttachment(attachment, { schema: ThemeSchema });
 * ```
 */
export function parseVariantAttachment<T = Record<string, unknown>>(
  attachment: string | undefined,
  options: ParseVariantAttachmentOptions<T> = {},
): T | null {
  const {
    schema = defaultVariantAttachmentSchema as z.ZodType<T>,
    throwOnError = false,
    onError,
  } = options;

  // Return null if attachment is undefined or empty
  if (!attachment || attachment.trim() === '') {
    return null;
  }

  try {
    // Parse JSON
    const parsed = JSON.parse(attachment);

    // Validate with Zod schema
    const validated = schema.parse(parsed);

    return validated;
  } catch (error) {
    const parseError =
      error instanceof Error
        ? error
        : new Error('Failed to parse variant attachment');

    // Call custom error handler if provided
    if (onError) {
      onError(parseError);
    }

    // Throw or return null based on options
    if (throwOnError) {
      const error = new Error(
        `Failed to parse variant attachment: ${parseError.message}`,
      );
      // Attach the original error for debugging
      (error as Error & { cause?: Error }).cause = parseError;
      throw error;
    }

    return null;
  }
}

/**
 * Type-safe wrapper for parsing variant attachments with a schema
 * This is a convenience function that enforces schema usage
 *
 * @param attachment The JSON string from variantAttachment
 * @param schema Zod schema for validation
 * @param options Additional parsing options
 * @returns Parsed and validated data, or null if parsing/validation fails
 *
 * @example
 * ```typescript
 * const ThemeSchema = z.object({
 *   primaryColor: z.string(),
 *   fontSize: z.number(),
 * });
 *
 * const theme = parseVariantAttachmentWithSchema(
 *   attachment,
 *   ThemeSchema
 * );
 *
 * if (theme) {
 *   // TypeScript knows theme is { primaryColor: string; fontSize: number }
 *   console.log(theme.primaryColor);
 * }
 * ```
 */
export function parseVariantAttachmentWithSchema<T>(
  attachment: string | undefined,
  schema: z.ZodType<T>,
  options: Omit<ParseVariantAttachmentOptions<T>, 'schema'> = {},
): T | null {
  return parseVariantAttachment(attachment, { ...options, schema });
}
